import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'

// ── Context ────────────────────────────────────────────────────────────────────

export interface ProfileDrawerContextValue {
  isOpen: boolean
  open: () => void
  close: () => void
}

export const ProfileDrawerContext =
  createContext<ProfileDrawerContextValue | null>(null)

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Access the global ProfileDrawer open/close controls.
 *
 * Must be called inside a component tree that has <ProfileDrawer /> mounted
 * (App.tsx root). TopBar avatar tap (W3W4 P2-a) calls `open()` here.
 */
export function useProfileDrawer(): ProfileDrawerContextValue {
  const ctx = useContext(ProfileDrawerContext)
  if (ctx === null) {
    throw new Error(
      'useProfileDrawer must be used within a component tree that mounts <ProfileDrawer />',
    )
  }
  return ctx
}

// ── State factory ──────────────────────────────────────────────────────────────

export function useProfileDrawerState(): ProfileDrawerContextValue {
  const [isOpen, setIsOpen] = useState(false)

  const open = useCallback((): void => {
    setIsOpen(true)
  }, [])

  const close = useCallback((): void => {
    setIsOpen(false)
  }, [])

  return { isOpen, open, close }
}

// ── Provider ──────────────────────────────────────────────────────────────────

/**
 * Wrap the part of the tree that needs to call useProfileDrawer().
 * The drawer body itself (<ProfileDrawer />) is rendered as a sibling INSIDE
 * this provider so both producer (body) and consumers (TopBar avatar via
 * AppShell) share the same context instance.
 */
export function ProfileDrawerProvider({
  children,
}: {
  children: ReactNode
}): ReactElement {
  const ctx = useProfileDrawerState()
  return (
    <ProfileDrawerContext.Provider value={ctx}>
      {children}
    </ProfileDrawerContext.Provider>
  )
}
